import useReveal from '../hooks/useReveal';
import SectionHead from './SectionHead';
import SitePreview from './SitePreview';
import { CASES } from '../data/cases';
import type { Case as CaseType } from '../data/cases';

/**
 * Transformation — two honest case studies.
 *
 * Data lives in ../data/cases.tsx. This file is presentation-only:
 * renders the section header and defers each case to <Case>.
 *
 * Framing reminder: no fabricated metrics. The "wins" list describes
 * observable design decisions a visitor can verify by clicking through
 * to the real site.
 */
export default function Transformation() {
  return (
    <section id="work" className="section demo">
      <div className="wrap">
        <SectionHead
          eyebrow="Recent work"
          title={
            <>
              Two projects. <em className="serif">Both real.</em>
            </>
          }
          body="Each project below is the actual live site, embedded. Scroll inside to explore, or click Visit to open it full-screen."
        />

        {CASES.map((c) => (
          <Case key={c.id} data={c} />
        ))}
      </div>
    </section>
  );
}

function Case({ data }: { data: CaseType }) {
  const ref = useReveal<HTMLElement>();

  const info = (
    <div className="case-info">
      <p className="kicker">{data.kicker}</p>
      <h3>
        {data.title} <em className="serif">— {data.titleEm}</em>
      </h3>
      <p>{data.body}</p>
      <ul className="case-meta" aria-label="Project tags">
        {data.tags.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
      <ul className="case-wins">
        {data.wins.map(([h, b]) => (
          <li key={h}>
            <span className="check" aria-hidden="true">
              <CheckIcon />
            </span>
            <span>
              <b>{h}</b> — {b}
            </span>
          </li>
        ))}
      </ul>
      <p className="case-url">
        <a href={`https://${data.url}`} target="_blank" rel="noopener noreferrer">
          {data.url} <span aria-hidden="true">↗</span>
        </a>
      </p>
    </div>
  );

  const viz = (
    <div className="case-viz">
      <SitePreview
        src={data.embedUrl}
        label={data.url}
        title={`${data.title} — live site preview`}
        aspect="16/10"
        staticImg={data.staticImg}
        staticImg2x={data.staticImg2x}
      />
    </div>
  );

  return (
    <article ref={ref} className="case reveal" data-flip={data.flip ? 'true' : 'false'}>
      {data.flip ? (
        <>
          {viz}
          {info}
        </>
      ) : (
        <>
          {info}
          {viz}
        </>
      )}
    </article>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}
